export const GANTT_ORDER_EDIT_ACTION = "gantt_source_backed_save";
const ACTION = GANTT_ORDER_EDIT_ACTION;
const FIELD_LABELS = {
  loading_window_start: "Heure de chargement",
  loading_priority: "Priorité de chargement",
  delivery_window_start: "Heure de livraison",
  delivery_priority: "Priorité de livraison",
  loading_silo: "Silo",
  building: "Bâtiment",
};

function text(value) {
  return typeof value === "string" ? value.trim() : "";
}

function clip(value, limit) {
  return Array.from(value).slice(0, limit).join("");
}

function normalizedValue(value, field) {
  const raw = text(value);
  if (!raw) return null;
  if (field.endsWith("priority")) return raw.toUpperCase();
  if (field.endsWith("window_start")) {
    const time = raw.match(/(?:^|T| )(\d{2}:\d{2})(?::(\d{2})(\.\d+)?)?/);
    if (time) return `${time[1]}${time[3] || (time[2] && time[2] !== "00") ? `:${time[2]}${time[3] || ""}` : ""}`;
  }
  return raw;
}

export function normalizeOrderEditEvidence(raw, source) {
  if (!raw || raw.source !== source) return null;
  const result = { source, applied: source === "planning_gantt" && typeof raw.applied === "boolean" ? raw.applied : null,
    comparison_available: raw.comparison_available === true, changed_fields: [] };
  for (const key of ["order_id", "client_name", "delivery_id", "loading_step_id", "truck_id", "truck_name", "expected_revision", "expected_order_updated_at", "submitted_at", "dispatch_date"]) {
    if (text(raw[key])) result[key] = clip(text(raw[key]), key === "client_name" ? 500 : key === "submitted_at" ? 64 : key === "dispatch_date" ? 32 : 255);
  }
  if (!result.order_id) return null;
  if (["loading", "delivery", "order"].includes(raw.step_kind)) result.step_kind = raw.step_kind;
  if (["day", "evening"].includes(raw.dispatch_period)) result.dispatch_period = raw.dispatch_period;
  for (const field of Object.keys(FIELD_LABELS)) {
    const change = raw.changed_fields?.find?.((item) => item?.field === field);
    if (!change || !["before", "after"].every((key) => Object.hasOwn(change, key) && (change[key] === null || typeof change[key] === "string"))) continue;
    const bounded = { field };
    if (change.before_available === false) bounded.before_available = false;
    for (const key of ["before", "after"]) {
      bounded[key] = change[key] === null ? null : clip(change[key], 500);
      if (change[`${key}_truncated`] || (change[key] && bounded[key] !== change[key])) bounded[`${key}_truncated`] = true;
    }
    result.changed_fields.push(bounded);
  }
  return result;
}

export function normalizeOrderEditCapture(raw) {
  if (!raw || typeof raw !== "object") return null;
  const submission = normalizeOrderEditEvidence(raw.submission, "browser_submission");
  const canonical = normalizeOrderEditEvidence(raw.canonical, "planning_gantt");
  const failure = {};
  for (const key of ["message", "reason_code", "expected_revision", "current_revision", "expected_order_updated_at"]) {
    if (text(raw.failure?.[key])) failure[key] = clip(text(raw.failure[key]), key === "message" ? 2000 : 255);
  }
  for (const key of ["required_minutes", "available_minutes"]) {
    const value = raw.failure?.[key];
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) failure[key] = value;
  }
  if (typeof raw.failure?.allow_overtime === "boolean") failure.allow_overtime = raw.failure.allow_overtime;
  return submission || canonical || Object.keys(failure).length
    ? { ...(submission ? { submission } : {}), ...(canonical ? { canonical } : {}), ...(Object.keys(failure).length ? { failure } : {}) } : null;
}

export function buildOrderEditSubmission(order, payload, context = {}) {
  const changes = [];
  let complete = true;
  const compare = (field, before, after, available) => {
    if (!available) {
      complete = false;
      changes.push({ field, before: null, after: normalizedValue(after, field), before_available: false });
      return;
    }
    const oldValue = normalizedValue(before, field), newValue = normalizedValue(after, field);
    if (oldValue !== newValue) changes.push({ field, before: oldValue, after: newValue });
  };
  for (const field of ["loading_window_start", "loading_priority", "loading_silo", "building"]) {
    if (Object.hasOwn(payload, field)) compare(field, order?.[field], payload[field], Object.hasOwn(order || {}, field));
  }
  const delivery = payload.deliveries?.[0];
  const deliveryId = delivery?.delivery_id || context.delivery_id;
  const beforeDelivery = order?.deliveries?.find((item) => item.delivery_id === deliveryId);
  if (delivery) {
    for (const [field, key] of [["delivery_window_start", "window_start"], ["delivery_priority", "priority"]]) {
      if (Object.hasOwn(delivery, key)) compare(field, beforeDelivery?.[key], delivery[key], Boolean(beforeDelivery && Object.hasOwn(beforeDelivery, key)));
    }
  }
  return normalizeOrderEditEvidence({ ...context, source: "browser_submission", order_id: order?.order_id,
    client_name: order?.client_name, delivery_id: deliveryId, comparison_available: complete, changed_fields: changes }, "browser_submission");
}

export function buildOrderEditFeedback({ submission, diagnostics, httpStatus = null, message = "", success = false } = {}) {
  const edit = normalizeOrderEditCapture({ submission, canonical: diagnostics?.order_edit_details, failure: diagnostics?.order_edit_failure });
  const applied = edit?.canonical?.applied;
  const confirmed = applied === true || (success && httpStatus >= 200 && httpStatus < 300);
  const refused = !confirmed && httpStatus >= 400 && httpStatus < 500;
  const unknown = !confirmed && applied !== false && !refused;
  const status = confirmed ? success ? "success" : "partial" : refused ? "refused" : "failed";
  const title = confirmed ? success ? "Modification depuis le segment Gantt enregistrée" : "Modification Gantt enregistrée — affichage incomplet"
    : refused ? "Modification depuis le segment Gantt refusée"
      : unknown ? "Modification depuis le segment Gantt — résultat non confirmé" : "Échec de la modification depuis le segment Gantt";
  const presentation = buildPlanningOrderEditPresentation({ action: ACTION, diagnostics: {
    order_edit_details: edit?.canonical, order_edit_submission: edit?.submission,
  } });
  const context = edit?.submission || edit?.canonical;
  const identity = [presentation.identity, context?.truck_name ? `Camion ${context.truck_name}` : "",
    context?.step_kind === "loading" ? "Segment de chargement" : context?.step_kind === "delivery" ? "Segment de livraison" : ""].filter(Boolean).join(" · ");
  const lines = [identity, ...presentation.lines,
    ...presentation.notices.filter((notice) => notice.startsWith("Valeurs") || notice.startsWith("Comparaison")),
    `${success ? "Résultat" : "Motif"} : ${message}`,
    confirmed ? success ? "Modification appliquée." : "Modification appliquée ; affichage à vérifier."
      : unknown ? "État de la modification non confirmé." : "Modification non appliquée."];
  return { title, message: lines.filter(Boolean).join("\n"), status, edit };
}

function formatValue(value, truncated) {
  const label = text(value) || "Non renseigné";
  return truncated ? `${label}… [valeur tronquée]` : label;
}

function buildTitle(fields) {
  const labels = [];
  for (const [prefix, step] of [["loading", "chargement"], ["delivery", "livraison"]]) {
    const hasTime = fields.has(`${prefix}_window_start`);
    const hasPriority = fields.has(`${prefix}_priority`);
    if (hasTime && hasPriority) labels.push(`Heure + priorité de ${step}`);
    else if (hasTime) labels.push(`Heure de ${step}`);
    else if (hasPriority) labels.push(`Priorité de ${step}`);
  }
  if (fields.has("loading_silo")) labels.push("Silo");
  if (fields.has("building")) labels.push("Bâtiment");
  return labels.length ? `${labels.join(" + ")} · Gantt` : "";
}

export function buildPlanningOrderEditPresentation(item = {}) {
  const diagnostics = item?.diagnostics || {};
  const action = item?.operationAction || item?.operation_action || diagnostics.operation_action
    || item?.action || diagnostics.action;
  if (action !== ACTION) return null;
  const raw = diagnostics.order_edit_details;
  const canonical = raw && typeof raw === "object" && !Array.isArray(raw) && raw.source === "planning_gantt"
    ? raw : null;
  const submission = diagnostics.order_edit_submission || diagnostics.operation_feedback?.order_edit_submission;
  const details = canonical?.comparison_available === true ? canonical
    : submission?.source === "browser_submission" ? submission : canonical;
  const orderId = text(details?.order_id) || text(diagnostics.delivery_id);
  const clientName = text(details?.client_name);
  const identity = [
    orderId ? `Commande ${orderId}${details?.order_id_truncated ? "… [tronqué]" : ""}` : "Commande non précisée",
    clientName ? `${clientName}${details?.client_name_truncated ? "… [tronqué]" : ""}` : "",
  ].filter(Boolean).join(" — ");
  const changes = (Array.isArray(details?.changed_fields) ? details.changed_fields : [])
    .filter((change) => change && Object.hasOwn(FIELD_LABELS, change.field)
      && Object.hasOwn(change, "before") && Object.hasOwn(change, "after"));
  const byField = new Map(changes.map((change) => [change.field, change]));
  const lines = Object.keys(FIELD_LABELS).filter((field) => byField.has(field)).map((field) => {
    const change = byField.get(field);
    return `${FIELD_LABELS[field]} : ${change.before_available === false ? "Valeur précédente indisponible" : formatValue(change.before, change.before_truncated)} → ${formatValue(change.after, change.after_truncated)}`;
  });
  const notices = [];
  if (!details) notices.push("Détail des modifications non enregistré");
  else {
    if (details.source === "browser_submission") notices.push("Valeurs saisies dans le navigateur — comparaison serveur indisponible");
    if (canonical?.applied === false) notices.push("Modification demandée — non appliquée");
    else if (!canonical) notices.push("Application non confirmée par le serveur");
    if (details.comparison_available !== true) notices.push("Comparaison avant/après indisponible ou incomplète");
    else if (!lines.length) notices.push("Aucune valeur modifiée");
  }
  const title = !details ? "Priorité / horaire"
    : buildTitle(new Set(byField.keys())) || (details.comparison_available === true
      ? "Enregistrement commande · Gantt" : "Modification commande · Gantt");
  const failure = normalizeOrderEditCapture({ failure: diagnostics.order_edit_failure })?.failure;
  const diagnosticRows = [];
  const add = (label, value) => { if (value) diagnosticRows.push({ label, value }); };
  const context = submission?.source === "browser_submission" ? submission : details;
  add("Camion à la confirmation", [context?.truck_name, context?.truck_id].filter(Boolean).join(" · "));
  add("Segment", [({ loading: "Chargement", delivery: "Livraison", order: "Commande" })[context?.step_kind], context?.loading_step_id, context?.delivery_id].filter(Boolean).join(" · "));
  add("Confirmation", context?.submitted_at);
  add("Motif du refus / de l’échec", failure?.message);
  add("Règle bloquante", failure?.reason_code);
  for (const [key, label] of [["required_minutes", "Durée requise"], ["available_minutes", "Durée autorisée"]]) {
    const minutes = failure?.[key];
    if (Number.isFinite(minutes)) add(label, `${Math.floor(Math.round(minutes) / 60)} h ${String(Math.round(minutes) % 60).padStart(2, "0")} min (${minutes} min)`);
  }
  if (typeof failure?.allow_overtime === "boolean") add("Heures supplémentaires", failure.allow_overtime ? "Autorisées pour le calcul" : "Non autorisées pour le calcul");
  add("Révision attendue", failure?.expected_revision || context?.expected_revision);
  add("Révision courante", failure?.current_revision);
  add("Version de commande soumise", failure?.expected_order_updated_at || context?.expected_order_updated_at);
  return { title, identity, notices, lines, diagnosticRows };
}

export function buildPlanningOrderEditCopyText(item) {
  const presentation = buildPlanningOrderEditPresentation(item);
  return presentation ? [
    "## Modification depuis le Gantt", presentation.identity,
    ...presentation.notices, ...presentation.lines,
    ...presentation.diagnosticRows.map(({ label, value }) => `${label} : ${value}`),
  ].join("\n") : "";
}
